import React, { useId } from 'react';
import ReactMarkdown from 'react-markdown';
import { CtaButton } from '../CtaButton/CtaButton';
import type { CtaBehavior } from '../../utils/ctaBehavior';
import { hasCtaDestination } from '../../utils/ctaAction';
import type { CtaAction } from '../../utils/ctaAction';
import { inlineMarkdownRehypePlugins } from '../../utils/inlineMarkdown';
import { Image } from '../Image/Image';
import { blockAnchorAttributes } from '../../utils/blockAnchor';
import './PreFooter.scss';

/**
 * PreFooter - Pre-footer CTA section for Poly CMS
 * 
 * Bottom-of-page call-to-action with heading, description, CTA button,
 * optional NSI text, and optional image.
 * 
 * Desktop (≥1024): side-by-side layout (content left, image right)
 * Tablet/Mobile (<1024): stacked layout (image top, content below, centered)
 * Mobile (≤479): full-width CTA, centered text
 * 
 * Supports ReactMarkdown for description and nsiText (incentives).
 */

export type PreFooterBackground = 'white' | 'off-white';

/**
 * @polySemanticUsage About this block: a conversion-focused section — most commonly placed near
 * the bottom of the page but NOT restricted to that position. The "prefooter" styling (bold
 * headline, short reinforcement copy, prominent CTA button) can appear mid-page whenever the
 * source content uses that visual pattern to re-engage readers, regardless of its vertical
 * position. Match by visual style, not by placement alone.
 * Choose CTA button colors by action type, not by appearance. When the primary
 * action is registration (Sign up, Start selling, Get started), set ctaColorVariant to primary
 * (Smile Orange fill). When the primary action is not registration (e.g. Enroll your brand — the
 * "dark" CTA treatment), set ctaColorVariant to contrast (dark fill, white text). Secondary
 * actions (Learn more, Watch overview) take secondary (outlined: transparent background with a
 * dark border), typically on secondaryCtaColorVariant.
 * Dual-CTA rule: never migrate a decision-tree CTA pair — two buttons segmenting users
 * by account status, e.g. "Sign up" + "Log in to get started" — as two buttons. Keep only the
 * registration action as the CTA and move the existing-seller login line into ctaSubtext. A dual
 * CTA is acceptable only when the second button is a discovery action for the same
 * prospective-seller audience (e.g. "Start selling" + "Watch overview").
 * CTA-area text placement — these props render in fixed positions. In the single-button layout,
 * ctaSubtext and nsiText render as two full-width below-CTA text lines (same treatment as Hero
 * incentivesText/secondaryIncentivesText), ctaSubtext first; put pricing/fee clarification in
 * ctaSubtext and promotional/incentive copy in nsiText. In the dual-CTA layout ctaSubtext renders
 * as small text directly below the PRIMARY button only; secondaryCtaSubtext renders directly
 * below the SECONDARY button only — never swap them. ctaLabel/secondaryCtaLabel render ABOVE
 * their buttons in the dual-CTA layout only — the single-button layout drops both labels;
 * source text above CTAs that routes users by account status ("For new sellers" /
 * "Already selling?") must be removed, not migrated into these labels. nsiText renders below the
 * CTA only in the single-button layout — it is not rendered at all when secondaryCtaText is set,
 * so in a dual-CTA layout put incentive copy in ctaSubtext instead.
 * Link behavior: always leave ctaBehavior / secondaryCtaBehavior unset (same tab), even when the
 * source link opens in a new tab or is an External Link type. Migration never sets newTab;
 * editors add it by hand for the few exceptions. Never infer it from the destination host.
 */
export interface PreFooterProps extends React.HTMLAttributes<HTMLElement> {
  /**
   * Main heading
   * @polyLabel Headline
   */
  heading: string;
  
  /**
   * Description text (supports Markdown)
   * @polyType RichText
   * @polyHelp Description text (supports rich text formatting)
   */
  description?: string;
  
  /**
   * CTA button text
   * @polyLabel CTA Text
   */
  ctaText: string;
  
  /**
   * CTA button href. Required for Link, Watch video and Download actions; ignored when Action is Popup.
   * @polyType Url
   * @polyLabel CTA Link
   */
  ctaHref?: string;
  
  /**
   * CTA button color variant
   * @polyLabel CTA Color
   */
  ctaColorVariant?: 'primary' | 'contrast' | 'secondary';
  
  /**
   * CTA button size variant
   * @polyLabel CTA Size
   * @polyOptions Default (Large)=default,Medium=medium,Small=small
   */
  ctaSize?: 'default' | 'medium' | 'small';
  
  /**
   * CTA button ID
   * @polyLabel CTA ID
   * @polyTranslatable false
   */
  ctaId?: string;
  
  /**
   * CTA button ld-append for analytics
   * @polyLabel CTA LD Appendix
   * @polyTranslatable false
   */
  ctaLdAppendix?: string;
  
  /**
   * Whether the CTA opens in the current tab or a new one
   * @polyType SelectList
   * @polyOptions Same tab=sameTab,New tab=newTab
   * @polyHelp Same tab by default. Choose New tab only when the seller needs the destination open alongside this page (how-to articles), it is a download (PDF, app store), or leaving would break a flow or session. Sign up and Register always stay Same tab.
   * @polyDefault sameTab
   * @polyLabel CTA Behavior
   * @polyTranslatable false
   */
  ctaBehavior?: CtaBehavior;

  /**
   * What the CTA does.
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Popup opens the Popup block matching the Target Popup ID and does not use the CTA Link or Behavior fields.
   * @polyDefault link
   * @polyTranslatable false
   */
  ctaAction?: CtaAction;

  /**
   * Popup ID to open when CTA Action is Popup.
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  ctaTargetPopupId?: string;
  
  /**
   * Secondary CTA button text
   * @polyLabel Secondary CTA Text
   */
  secondaryCtaText?: string;
  
  /**
   * Secondary CTA button href
   * @polyType Url
   * @polyLabel Secondary CTA Link
   */
  secondaryCtaHref?: string;
  
  /**
   * Secondary CTA button color variant
   * @polyLabel Secondary CTA Color
   */
  secondaryCtaColorVariant?: 'primary' | 'contrast' | 'secondary';
  
  /**
   * Secondary CTA button ID
   * @polyLabel Secondary CTA ID
   * @polyTranslatable false
   */
  secondaryCtaId?: string;
  
  /**
   * Secondary CTA button ld-append for analytics
   * @polyLabel Secondary CTA LD Appendix
   * @polyTranslatable false
   */
  secondaryCtaLdAppendix?: string;
  
  /**
   * Whether the secondary CTA opens in the current tab or a new one
   * @polyType SelectList
   * @polyOptions Same tab=sameTab,New tab=newTab
   * @polyHelp Same tab by default. Choose New tab only when the seller needs the destination open alongside this page (how-to articles), it is a download (PDF, app store), or leaving would break a flow or session. Sign up and Register always stay Same tab.
   * @polyDefault sameTab
   * @polyLabel Secondary CTA Behavior
   * @polyTranslatable false
   */
  secondaryCtaBehavior?: CtaBehavior;

  /**
   * What the secondary CTA does.
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Popup opens the Popup block matching the Target Popup ID and does not use the CTA Link or Behavior fields.
   * @polyDefault link
   * @polyTranslatable false
   */
  secondaryCtaAction?: CtaAction;

  /**
   * Popup ID to open when Secondary CTA Action is Popup.
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  secondaryCtaTargetPopupId?: string;
  
  /**
   * Label above primary CTA (only rendered in dual-CTA layout)
   * @polyLabel CTA Label
   */
  ctaLabel?: string;
  
  /**
   * Label above secondary CTA (only rendered in dual-CTA layout)
   * @polyLabel Secondary CTA Label
   */
  secondaryCtaLabel?: string;
  
  /**
   * Small text below the secondary CTA in the dual-CTA layout (e.g., pricing info; supports Markdown)
   * @polyType RichText
   * @polyLabel Secondary CTA Subtext
   */
  secondaryCtaSubtext?: string;
  
  /**
   * Optional NSI incentives text (supports Markdown). Renders below the CTA in the
   * single-button layout only — not rendered when secondaryCtaText is set.
   * @polyType RichText
   * @polyLabel NSI Text
   * @polyHelp Incentives text below the CTA — only shown in the single-button layout (ignored when a secondary CTA is set)
   */
  nsiText?: string;
  
  /**
   * Text below the CTA (e.g., pricing info; supports Markdown). In the single-button layout it
   * renders as the first below-CTA text line, ahead of nsiText; in the dual-CTA layout it renders
   * as small text directly below the primary button.
   * @polyType RichText
   * @polyLabel CTA Subtext
   */
  ctaSubtext?: string;
  
  /**
   * Optional image URL (side image, used in primary/secondary layout)
   * @polyType Url
   * @polyLabel Image URL
   */
  imageUrl?: string;
  
  /** Optional image alt text */
  imageAlt?: string;
  
  /**
   * Background image URL — renders a full-bleed image behind the content
   * with a dark gradient overlay and backdrop blur.
   * Content text becomes white. Layout is always centered (no side image).
   * Mutually exclusive with `imageUrl` (backgroundImageUrl takes precedence).
   * @polyType Url
   * @polyLabel Background Image URL
   */
  backgroundImageUrl?: string;
  
  /** Background image alt text (for accessibility, rendered as aria-label) */
  backgroundImageAlt?: string;
  
  /**
   * Overlay 1 image URL
   * @polyType Url
   * @polyLabel Overlay 1 Image URL
   */
  overlay1Url?: string;
  /**
   * Overlay 1 alt text
   * @polyLabel Overlay 1 Alt Text
   */
  overlay1Alt?: string;
  /**
   * Overlay 1 position
   * @polyOptions Bottom Right=bottom-right,Bottom Left=bottom-left,Top Right=top-right,Top Left=top-left,Center=center
   */
  overlay1Position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left' | 'center';
  /**
   * Overlay 1 width
   * @polyTranslatable false
   */
  overlay1Width?: string;
  /**
   * Overlay 2 image URL
   * @polyType Url
   * @polyLabel Overlay 2 Image URL
   */
  overlay2Url?: string;
  /**
   * Overlay 2 alt text
   * @polyLabel Overlay 2 Alt Text
   */
  overlay2Alt?: string;
  /**
   * Overlay 2 position
   * @polyOptions Bottom Right=bottom-right,Bottom Left=bottom-left,Top Right=top-right,Top Left=top-left,Center=center
   */
  overlay2Position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left' | 'center';
  /**
   * Overlay 2 width
   * @polyTranslatable false
   */
  overlay2Width?: string;
  /**
   * Background style preset
   * @polyHelp Background style for the section
   * @polyOptions White=white,Off-White=off-white
   */
  background?: PreFooterBackground;

  /**
   * Content alignment — 'left' (default) or 'center'. When 'center', the
   * heading, description, and CTA group are center-aligned. Mobile and the
   * background-image variant center regardless of this prop.
   * @polyLabel Text Alignment
   * @polyOptions Left=left,Center=center
   */
  textAlign?: 'left' | 'center';
  /**
   * Anchor ID for in-page links: a link to `#<anchorId>` on the same page scrolls to this block.
   * Normalised to letters, numbers and dashes (spaces become dashes, a leading `#` and other
   * characters are dropped). Must be unique on the page. Migration: when any link on the source page points at `#<id>` and that id sits on the
   * source section this block replaces, copy the id here unchanged (without the `#`).
   * @polyType Text
   * @polyLabel Anchor ID
   * @polyHelp Lets in-page links such as #your-id jump to this block. Use letters, numbers and dashes (other characters are removed); must be unique on the page.
   * @polyTranslatable false
   */
  anchorId?: string;
}

const backgroundMap: Record<PreFooterBackground, string> = {
  'white': 'var(--poly-color-background-white)',
  'off-white': 'var(--poly-color-background-offWhite)',
};

export const PreFooter: React.FC<PreFooterProps> = ({
  heading,
  description,
  ctaText,
  ctaHref,
  ctaColorVariant = 'primary',
  ctaSize,
  ctaId,
  ctaLdAppendix,
  ctaBehavior,
  ctaAction,
  ctaTargetPopupId,
  nsiText,
  secondaryCtaText,
  secondaryCtaHref,
  secondaryCtaColorVariant = 'secondary',
  secondaryCtaId,
  secondaryCtaLdAppendix,
  secondaryCtaBehavior,
  secondaryCtaAction,
  secondaryCtaTargetPopupId,
  ctaLabel,
  secondaryCtaLabel,
  secondaryCtaSubtext,
  ctaSubtext,
  imageUrl,
  imageAlt,
  backgroundImageUrl,
  backgroundImageAlt,
  overlay1Url,
  overlay1Alt,
  overlay1Position,
  overlay1Width,
  overlay2Url,
  overlay2Alt,
  overlay2Position,
  overlay2Width,
  background = 'off-white',
  textAlign = 'left',
  className,
  anchorId,
  ...divProps
}) => {
  const hasBackgroundImage = !!backgroundImageUrl;
  const bgColor = hasBackgroundImage ? undefined : backgroundMap[background];

  // Background image variant always uses centered layout, no side image
  const showSideImage = !hasBackgroundImage && !!imageUrl;

  const hasPrimaryCta = Boolean(ctaText && hasCtaDestination(ctaHref, ctaAction, ctaTargetPopupId));
  const hasSecondaryCta = Boolean(
    secondaryCtaText && hasCtaDestination(secondaryCtaHref, secondaryCtaAction, secondaryCtaTargetPopupId),
  );

  // Dual-CTA defaults to medium (48px) buttons
  const resolvedCtaSize = ctaSize || (hasSecondaryCta ? 'medium' : undefined);

  // The single-CTA layout renders ctaSubtext as a sibling line rather than inside
  // CtaButton, so the button has to point at it explicitly for screen readers to
  // announce the pricing line as its description.
  const generatedId = useId();
  const ctaSubtextId = ctaSubtext ? `${ctaId ?? generatedId}-cta-subtext` : undefined;

  const sectionClasses = [
    'pre-footer',
    // The background-image variant lays copy over a photo behind a dark gradient,
    // so it needs light text and Cloudscape's dark-mode palette.
    hasBackgroundImage ? 'pre-footer--bg-image pre-footer--dark awsui-polaris-dark-mode' : '',
    !showSideImage && !hasBackgroundImage ? 'pre-footer--no-image' : '',
    textAlign === 'center' ? 'pre-footer--centered' : '',
    className || '',
  ].filter(Boolean).join(' ');

  return (
    <section
      {...divProps}
      {...blockAnchorAttributes(anchorId)}
      className={sectionClasses}
      aria-label={hasBackgroundImage && backgroundImageAlt ? backgroundImageAlt : undefined}
    >
      <div
        className="pre-footer__card"
        style={bgColor ? { backgroundColor: bgColor } : undefined}
      >
        {/* Background image with gradient overlay and blur */}
        {hasBackgroundImage && (
          <>
            <div className="pre-footer__bg-image" aria-hidden="true">
              <Image
                bare
                url={backgroundImageUrl}
                alt=""
                className="pre-footer__bg-image-img"
                aria-hidden="true"
              />
              <div className="pre-footer__bg-overlay" />
            </div>
            <div className="pre-footer__bg-blur" aria-hidden="true" />
          </>
        )}

        <div className="pre-footer__content">
          <div className="pre-footer__text-group">
            <h2 className="pre-footer__heading">{heading}</h2>
            {description && (
              <div className="pre-footer__description">
                <ReactMarkdown rehypePlugins={inlineMarkdownRehypePlugins}>{description}</ReactMarkdown>
              </div>
            )}
          </div>

          <div className={`pre-footer__cta-group ${hasSecondaryCta ? 'pre-footer__cta-group--dual' : ''}`}>
            {hasSecondaryCta ? (
              <>
                {hasPrimaryCta && (
                  <div className="pre-footer__cta-column">
                    {ctaLabel && <span className="pre-footer__cta-label">{ctaLabel}</span>}
                    <CtaButton
                      colorVariant={ctaColorVariant}
                      size={resolvedCtaSize}
                      href={ctaHref}
                      id={ctaId}
                      ldAppendix={ctaLdAppendix}
                      behavior={ctaBehavior}
                      action={ctaAction}
                      popupTargetId={ctaTargetPopupId}
                      subtext={ctaSubtext}
                    >
                      {ctaText}
                    </CtaButton>
                  </div>
                )}
                <div className="pre-footer__cta-column">
                  {secondaryCtaLabel && <span className="pre-footer__cta-label">{secondaryCtaLabel}</span>}
                  <CtaButton
                    colorVariant={secondaryCtaColorVariant}
                    size={resolvedCtaSize}
                    href={secondaryCtaHref}
                    id={secondaryCtaId}
                    ldAppendix={secondaryCtaLdAppendix}
                    behavior={secondaryCtaBehavior}
                    action={secondaryCtaAction}
                    popupTargetId={secondaryCtaTargetPopupId}
                    subtext={secondaryCtaSubtext}
                  >
                    {secondaryCtaText}
                  </CtaButton>
                </div>
              </>
            ) : (
              <>
                {hasPrimaryCta && (
                  <CtaButton
                    colorVariant={ctaColorVariant}
                    size={resolvedCtaSize}
                    href={ctaHref}
                    id={ctaId}
                    ldAppendix={ctaLdAppendix}
                    behavior={ctaBehavior}
                    action={ctaAction}
                    popupTargetId={ctaTargetPopupId}
                    ariaDescribedby={ctaSubtextId}
                  >
                    {ctaText}
                  </CtaButton>
                )}

                {(ctaSubtext || nsiText) && (
                  <div className="pre-footer__below-cta">
                    {ctaSubtext && (
                      <>
                        {/* Same rule as CtaButton: the description target is a hidden text-only copy so
                            aria-describedby references plain text; the visible line keeps its links. */}
                        <span id={ctaSubtextId} className="poly-visually-hidden" aria-hidden="true">
                          <ReactMarkdown rehypePlugins={inlineMarkdownRehypePlugins} allowedElements={[]} unwrapDisallowed>
                            {ctaSubtext}
                          </ReactMarkdown>
                        </span>
                        <div className="pre-footer__nsi-text">
                          <ReactMarkdown rehypePlugins={inlineMarkdownRehypePlugins}>{ctaSubtext}</ReactMarkdown>
                        </div>
                      </>
                    )}
                    {nsiText && (
                      <div className="pre-footer__nsi-text">
                        <ReactMarkdown rehypePlugins={inlineMarkdownRehypePlugins}>{nsiText}</ReactMarkdown>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {showSideImage && (
          <div className="pre-footer__image">
            <Image
              url={imageUrl}
              alt={imageAlt ?? heading}
              objectFit='cover'
              overlay1Url={overlay1Url}
              overlay1Alt={overlay1Alt}
              overlay1Position={overlay1Position}
              overlay1Width={overlay1Width}
              overlay2Url={overlay2Url}
              overlay2Alt={overlay2Alt}
              overlay2Position={overlay2Position}
              overlay2Width={overlay2Width}
            />
          </div>
        )}
      </div>
    </section>
  );
};
